import { pickLocale, type Characteristic, type Component, type LocalizedString } from '../schema';
import type { RuleError } from '../rules/types';
import type { SelectionsMap } from '../state/selections';
import type { VisibilityRules } from '../state/effectiveSelections';
import { isCharacteristicVisible } from '../rules/RuleResultsContext';
import type { MessageKey } from '../i18n/messages';
import { formatBound } from '../i18n/formatBound';

type TranslateFn = (key: MessageKey, params?: Record<string, string | number>) => string;

export interface ValidationError {
    characteristicId: number;
    altTitle: string;
    /** Stable machine code. Wider than before because we now distinguish
     * each named legacy validator for tailored messages and tests. */
    code:
        | 'required'
        | 'pattern'
        | 'range'
        | 'length'
        | 'numeric'
        | 'integer'
        | 'gt-zero'
        | 'gte-zero'
        | 'email'
        | 'url'
        | 'zip'
        | 'phone'
        | 'iban'
        | 'bic'
        | 'vat'
        | 'code'
        | 'alpha'
        | 'alphanum'
        | 'data'
        | 'lowercase'
        | 'uppercase'
        /** An error rule (Regeltyp „Fehler / Hinweis“, Meldungstyp Fehler) matched. */
        | 'rule'
        /** The host's step guard rejected the step (`setStepGuard`). */
        | 'guard';
    /** Pre-translated human message for the active locale. */
    message: string;
}

/**
 * Legacy named validators (configurator/option/abstract.js) ported as regex.
 * Locale-aware validators (zip, phone, vat) in legacy depended on
 * Justselling_Configento_App_Locale.getCountry() — V3 doesn't have that yet,
 * so we use sensible defaults that cover the bulk of EU customers (DE/AT/
 * CH/FR/IT/ES). The patterns aim for ~95% behavioural parity with the
 * legacy FormValidation.io plugin defaults.
 *
 * Numbers and "letters" allow common German diacritics (äöüß and case
 * variants) — the legacy frontend made the same choice in its regex.
 */
const VALIDATORS: Record<string, RegExp> = {
    // Direct from legacy option/abstract.js:1940-1974
    'validate-alpha':     /^[a-zA-ZäÄöÖüÜß]+$/,
    'validate-code':      /^[a-z0-9_äöü]+$/,
    'validate-lowercase': /^[a-z0-9_äöü]+$/,
    'validate-uppercase': /^[A-Z0-9_ÄÖÜ]+$/,
    'validate-alphanum':  /^[a-zA-Z0-9äÄöÖüÜß]+$/,
    'validate-data':      /^[a-zA-Z0-9_äÄöÖüÜß]+$/,
    'validate-digits':    /^\d+$/,

    // Email (FormValidation.io default — RFC-light, no quoting)
    'validate-email':     /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
    // URI (FormValidation.io default — http/https + non-empty host)
    'validate-uri':       /^https?:\/\/[^\s]+$/i,
    // ZIP — country-aware in legacy; here we accept any 3–10-digit/letter
    // string with optional spaces or dashes (covers DE 5-digit, NL 4+2,
    // GB alphanumeric, US 5+4, etc).
    'validate-zip':       /^[A-Za-z0-9][A-Za-z0-9 -]{2,9}$/,
    // Phone — "lax" in legacy, matches digits + common separators.
    'validate-phoneLax':  /^[+\d][\d\s\-/().]{4,}$/,
    // IBAN — country prefix (2 letters) + 2 check digits + 11..30 alnum.
    // FormValidation.io does mod-97; here we just shape-check, which catches
    // the typical user-error case (typos, wrong length). Strict mod-97 would
    // pull in ~400 bytes of code for negligible benefit.
    'validate-iban':      /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/,
    // BIC — 4 bank + 2 country + 2 location + optional 3 branch.
    'validate-bic':       /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
    // VAT — country prefix (2 letters) + 2..12 alnum. Country-specific
    // checksums in the legacy plugin; shape check is the safe minimum.
    'validate-vat':       /^[A-Z]{2}[A-Z0-9]{2,12}$/i,
};

/**
 * Numbers don't share a single regex — locale-aware decimal separators
 * (de_DE → "1,5", en_US → "1.5") and the four numeric variants need
 * dedicated parsing. {@link validateNumeric} handles all of them.
 */
const NUMERIC_VALIDATORS = new Set([
    'validate-number',
    'validate-digits',
    'validate-greater-than-zero',
    'validate-zero-or-greater',
]);

/** Heuristic: which HTML input `type` matches a `validate-*` name. */
export function inputTypeForValidator(name: string | undefined): 'text' | 'email' | 'url' | 'tel' | 'number' {
    if (!name) return 'text';
    if (name === 'validate-email') return 'email';
    if (name === 'validate-uri' || name === 'validate-url') return 'url';
    if (name === 'validate-phoneLax' || name === 'validate-phone') return 'tel';
    if (NUMERIC_VALIDATORS.has(name)) return 'number';
    return 'text';
}

/**
 * Run client-side validation across every visible Characteristic.
 *
 * The function is pure — it doesn't touch the DOM, so callers (an
 * ActionButton click, a Wizard "next" press) decide what to do with the
 * results: show inline errors, scroll-to-first, block the submit, etc.
 *
 * Skipped:
 *   - Characteristics the customer can't see — see {@link isCharacteristicVisible}
 *   - calculated types (expression, combi, matrix, …) — no user input
 *   - actionbutton / static / chart — render-only
 */
export function validateConfiguration(
    component: Component,
    selections: SelectionsMap,
    rules: VisibilityRules & { errors?: ReadonlyArray<RuleError> },
    t: TranslateFn,
    locale: string | undefined,
): ValidationError[] {
    const errors: ValidationError[] = [];
    // A matching error rule blocks Add-to-Cart like a missing required field
    // does — legacy refuses the cart with the rule's message
    // (rules/rule.js process, errorExecution === 'cart'). Warnings don't.
    for (const ruleError of rules.errors ?? []) {
        if (ruleError.severity !== 'error') continue;
        const target = ruleError.characteristic
            ? component.groups.flatMap((g) => g.characteristics).find((c) => c.altTitle === ruleError.characteristic)
            : undefined;
        errors.push({
            characteristicId: target?.id ?? 0,
            altTitle: target?.altTitle ?? '',
            code: 'rule',
            message: messageText(ruleError.message, locale),
        });
    }
    for (const group of component.groups) {
        // An admin-hidden Group renders nothing, so its required fields
        // can't block Add-to-Cart (legacy: jQuery-validate skips hidden DOM).
        if (group.hidden) continue;
        errors.push(
            ...validateCharacteristics(group.characteristics, selections, rules, t, locale),
        );
    }
    return errors;
}

/**
 * Step-scoped variant: validates only the supplied Characteristics. Used by
 * the Wizard "Weiter" path so customers can't skip past required fields on
 * the current step.
 */
export function validateCharacteristics(
    characteristics: ReadonlyArray<Characteristic>,
    selections: SelectionsMap,
    rules: VisibilityRules,
    t: TranslateFn,
    locale: string | undefined,
): ValidationError[] {
    const errors: ValidationError[] = [];
    for (const c of characteristics) {
        // Same predicate the UI renders by — a rule-removed Characteristic,
        // an admin-hidden one, and one whose every Value got blacklisted are
        // all invisible, so none of them may block Add-to-Cart. The last case
        // is the one a plain `hiddenCharacteristics` check misses: a required
        // Characteristic that a rule emptied would otherwise raise an error
        // the customer has no way to clear.
        if (!isCharacteristicVisible(c, rules)) continue;
        errors.push(...validateOne(c, selections, t, locale));
    }
    return errors;
}

function validateOne(
    c: Characteristic,
    selections: SelectionsMap,
    t: TranslateFn,
    locale: string | undefined,
): ValidationError[] {
    const sel = selections[c.id];
    const field = fieldName(c, locale);

    if (c.type === 'captcha') {
        const payload = sel?.kind === 'captcha' ? sel.payload : '';
        if (payload.trim().length === 0) {
            return [error(c, 'required', t('validation.captcha_required'))];
        }
        return [];
    }

    if (c.type === 'fileupload') {
        // Legacy counts the files Dropzone accepted against `min_files`;
        // a required upload needs at least one.
        const count = sel?.kind === 'files' ? sel.files.length : 0;
        const min = Math.max(c.minFiles ?? 0, c.isRequired ? 1 : 0);
        if (count >= min) return [];
        return [min > 1
            ? error(c, 'required', t('validation.min_files', { n: min, field }))
            : error(c, 'required', t('validation.required', { field }))];
    }

    if (c.type === 'text' || c.type === 'area') {
        const value = sel?.kind === 'text' ? sel.value : '';
        if (c.isRequired && value.trim().length === 0) {
            return [error(c, 'required', t('validation.required', { field }))];
        }
        if (value.length === 0) return [];
        const errs: ValidationError[] = [];

        if (c.type === 'text' && c.textValidate) {
            errs.push(...validateNamedFormat(c, value, t, field, locale));
        }

        // String length applies to area + text without a numeric validator.
        // Area has no textValidate so it's always treated as string-typed.
        const isNumericValidator =
            c.type === 'text' && NUMERIC_VALIDATORS.has(c.textValidate ?? '');
        if (!isNumericValidator) {
            const minLen = toNumOrU(c.minStringLength);
            const maxLen = toNumOrU(c.maxStringLength);
            if (minLen !== undefined && value.length < minLen) {
                errs.push(error(c, 'length', t('validation.min_length', { value: formatBound(minLen, locale), field })));
            }
            if (maxLen !== undefined && value.length > maxLen) {
                errs.push(error(c, 'length', t('validation.max_length', { value: formatBound(maxLen, locale), field })));
            }
        }
        return errs;
    }

    if ('isRequired' in c && c.isRequired) {
        // Selection-style required check.
        const ok =
            sel !== undefined &&
            ((sel.kind === 'single' && sel.valueId !== null) ||
                (sel.kind === 'multi' && sel.valueIds.length > 0) ||
                (sel.kind === 'quantities' && Object.values(sel.quantities).some((q) => q > 0)));
        if (!ok) {
            return [error(c, 'required', t('validation.required', { field }))];
        }
    }
    return [];
}

function validateNamedFormat(
    c: Extract<Characteristic, { type: 'text' }>,
    value: string,
    t: TranslateFn,
    field: string,
    locale: string | undefined,
): ValidationError[] {
    const name = c.textValidate;
    if (!name) return [];
    const errs: ValidationError[] = [];

    // ── Numeric family ───────────────────────────────────────────────────
    if (NUMERIC_VALIDATORS.has(name)) {
        // Accept "1,5" and "1.5" alike — legacy did the same via the
        // `data-fv-numeric-decimalseparator` setting for DE/FR/IT locales.
        const normalised = value.replace(',', '.').trim();

        if (name === 'validate-digits') {
            if (!/^\d+$/.test(normalised)) {
                errs.push(error(c, 'integer', t('validation.validate-digits', { field })));
                return errs;
            }
        } else {
            // Other numeric validators allow decimals.
            if (!/^-?\d+(?:\.\d+)?$/.test(normalised)) {
                errs.push(error(c, 'numeric', t('validation.numeric', { field })));
                return errs;
            }
        }

        const n = Number(normalised);
        if (name === 'validate-greater-than-zero' && !(n > 0)) {
            errs.push(error(c, 'gt-zero', t('validation.validate-greater-than-zero', { field })));
        }
        if (name === 'validate-zero-or-greater' && !(n >= 0)) {
            errs.push(error(c, 'gte-zero', t('validation.validate-zero-or-greater', { field })));
        }

        // Numeric range from min_value / max_value applies to every numeric
        // validator. Mirrors legacy `validateNumberRange` block.
        const min = toNumOrU(c.minValue);
        const max = toNumOrU(c.maxValue);
        if (min !== undefined && n < min) {
            errs.push(error(c, 'range', t('validation.min', { value: formatBound(c.minValue, locale), field })));
        }
        if (max !== undefined && n > max) {
            errs.push(error(c, 'range', t('validation.max', { value: formatBound(c.maxValue, locale), field })));
        }
        return errs;
    }

    // ── Regex-based family ───────────────────────────────────────────────
    const pattern = VALIDATORS[name];
    if (!pattern) {
        // Unknown validator name — skip silently. Mirrors legacy default
        // case which just logged a debug message and let the value through.
        return errs;
    }
    if (!pattern.test(value)) {
        const code = NAMED_TO_CODE[name] ?? 'pattern';
        // Each named validator has its own translation key; the fallback
        // `validation.pattern` lands when we don't ship a tailored one.
        const messageKey = `validation.${name}` as MessageKey;
        errs.push(error(c, code, tWithFallback(t, messageKey, { field })));
    }
    return errs;
}

const NAMED_TO_CODE: Record<string, ValidationError['code']> = {
    'validate-alpha':     'alpha',
    'validate-code':      'code',
    'validate-lowercase': 'lowercase',
    'validate-uppercase': 'uppercase',
    'validate-alphanum':  'alphanum',
    'validate-data':      'data',
    'validate-email':     'email',
    'validate-uri':       'url',
    'validate-zip':       'zip',
    'validate-phoneLax':  'phone',
    'validate-iban':      'iban',
    'validate-bic':       'bic',
    'validate-vat':       'vat',
};

/**
 * Translate with a graceful fallback — when we extend the validator list
 * without shipping every translation key, fall back to the generic
 * `validation.pattern` message instead of rendering the raw key.
 */
function tWithFallback(
    t: TranslateFn,
    key: MessageKey,
    params: Record<string, string | number>,
): string {
    const translated = t(key, params);
    if (translated && translated !== key) return translated;
    return t('validation.pattern' as MessageKey, params);
}

function fieldName(c: Characteristic, locale: string | undefined): string {
    return pickLocale(c.title, locale) ?? c.altTitle ?? `#${c.id}`;
}

function error(
    c: Characteristic,
    code: ValidationError['code'],
    message: string,
): ValidationError {
    return {
        characteristicId: c.id,
        altTitle: c.altTitle,
        code,
        message,
    };
}

function toNumOrU(v: string | undefined): number | undefined {
    if (v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
}

function messageText(message: string | LocalizedString, locale: string | undefined): string {
    if (typeof message === 'string') return message;
    const direct = locale ? message[locale] : undefined;
    if (typeof direct === 'string' && direct.length > 0) return direct;
    for (const v of Object.values(message)) {
        if (typeof v === 'string' && v.length > 0) return v;
    }
    return '';
}
